import { type FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../../lib/api';
import { Alert, ErrorMessage, Field, PageTitle } from '../../components/ui';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const code = params.get('code');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      if (code) await api.post('/api/v1/auth/password/reset', { email, code, password });
      else await api.post('/api/v1/auth/password/forgot', { email });
      setDone(true);
    } catch (e) {
      setError(e);
    }
  }

  if (done) {
    return (
      <section className="auth-card">
        <PageTitle>{code ? 'Hasło zmienione' : 'Sprawdź skrzynkę'}</PageTitle>
        <Alert kind="success">
          {code
            ? 'Możesz zalogować się nowym hasłem.'
            : 'Jeśli konto z tym adresem istnieje, wysłaliśmy link do ustawienia nowego hasła.'}
        </Alert>
        <Link to="/logowanie">Przejdź do logowania</Link>
      </section>
    );
  }

  return (
    <section className="auth-card">
      <PageTitle>{code ? 'Ustaw nowe hasło' : 'Nie pamiętasz hasła?'}</PageTitle>
      <ErrorMessage error={error} />
      <form onSubmit={(e) => void submit(e)}>
        <Field label="E-mail" name="email" type="email" autoComplete="username" value={email} onChange={setEmail} required />
        {code && (
          <Field
            label="Nowe hasło"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            hint="Co najmniej 12 znaków."
            required
          />
        )}
        <button type="submit">{code ? 'Zapisz hasło' : 'Wyślij link'}</button>
      </form>
    </section>
  );
}

/** Confirms the address after self-registration (/potwierdz-email) or an e-mail change (/konto/potwierdz-email). */
export function ConfirmEmailPage({ change = false }: { change?: boolean }) {
  const [params] = useSearchParams();
  const [state, setState] = useState<'pending' | 'ok' | 'error'>('pending');

  useEffect(() => {
    const body = change
      ? { userId: params.get('userId'), email: params.get('email'), code: params.get('code') }
      : { userId: params.get('userId'), code: params.get('code') };
    api
      .post(change ? '/api/v1/auth/email/change/confirm' : '/api/v1/auth/email/confirm', body)
      .then(() => setState('ok'))
      .catch(() => setState('error'));
  }, [change, params]);

  return (
    <section className="auth-card">
      <PageTitle>Potwierdzenie adresu e-mail</PageTitle>
      {state === 'pending' && <p role="status">Sprawdzamy link…</p>}
      {state === 'ok' && <Alert kind="success">Adres e-mail został potwierdzony.</Alert>}
      {state === 'error' && <Alert>Link jest nieprawidłowy lub wygasł.</Alert>}
      <Link to="/logowanie">Przejdź do logowania</Link>
    </section>
  );
}

export function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '', phone: '' });
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form) => (value: string) => setForm({ ...form, [key]: value });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api.post('/api/v1/auth/register', { ...form, phone: form.phone || null });
      setDone(true);
    } catch (e) {
      setError(e);
    }
  }

  if (done) {
    return (
      <section className="auth-card">
        <PageTitle>Sprawdź skrzynkę</PageTitle>
        <Alert kind="success">Wysłaliśmy link do potwierdzenia adresu e-mail.</Alert>
      </section>
    );
  }

  return (
    <section className="auth-card">
      <PageTitle>Załóż konto</PageTitle>
      <ErrorMessage error={error} />
      <form onSubmit={(e) => void submit(e)}>
        <Field label="Imię" name="firstName" autoComplete="given-name" value={form.firstName} onChange={set('firstName')} required />
        <Field label="Nazwisko" name="lastName" autoComplete="family-name" value={form.lastName} onChange={set('lastName')} required />
        <Field label="E-mail" name="email" type="email" autoComplete="email" value={form.email} onChange={set('email')} required />
        <Field label="Telefon (opcjonalnie)" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} />
        <Field label="Hasło" name="password" type="password" autoComplete="new-password" value={form.password} onChange={set('password')} hint="Co najmniej 12 znaków." required />
        <button type="submit">Załóż konto</button>
      </form>
    </section>
  );
}
